import type { ColumnDef, Row, SortingState } from "@tanstack/react-table";
import type { DateRange } from "react-day-picker";

import { Button, DateRangePicker, FormInput, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Table, TableActionsDropdown, Text } from "@sentricmusic/ui";
import { format } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaFile, FaFileCsv, FaFilePdf } from "react-icons/fa";

import type { AccountStatementData } from "@/api/queries";

import {
  useGetMyProfile,
  useGetMyStatementFiles,
  useGetMyStatements,
} from "@/api/queries";
import currencies from "@/helpers/currency";
import { sendStatementInfo } from "@/helpers/helpers";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import { formatCurrencyString } from "./utils/statementUtils";

function getTypeIcon(mime: string): React.ReactElement {
  switch (mime) {
    case "application/pdf":
      return (
        <FaFilePdf />
      );
    case "text/csv":
      return (
        <FaFileCsv />
      );
    default:
      return (
        <FaFile />
      );
  }
}

type StatementFileActionsCellProps = {
  suid: string;
  aac: string;
  userEmail: string | undefined;
  previewStatements: boolean;
};

function StatementFileActionsCell({ suid, aac, userEmail, previewStatements }: StatementFileActionsCellProps) {
  const { t } = useTranslation();
  // Only fetch the file list once the dropdown is actually opened, matching
  // the on-demand fetch this replaced (files were previously loaded only
  // when a row was expanded).
  const [isOpen, setIsOpen] = useState(false);
  const files = useGetMyStatementFiles({ aac, suid, previewstatements: previewStatements, enabled: isOpen });

  const handleDocumentOpen = useCallback(
    (url: string, filename: string, mimetype: string) => {
      window.open(url, "_blank");
      (sendStatementInfo as (args: { eventName: string; properties: Record<string, unknown> }) => void)({
        eventName: "pe14489857_april_2024_statement_download_pdf",
        properties: {
          email: userEmail,
          documentName: filename,
          documentType: mimetype,
        },
      });
    },
    [userEmail],
  );

  const fileList = files.data?.statementsFiles
    ? [...files.data.statementsFiles].sort(
        (a, b) => a.mimetype.localeCompare(b.mimetype) || b.filename.localeCompare(a.filename),
      )
    : [];

  return (
    <TableActionsDropdown ariaLabel={String(t("other:view-files", "View Files"))} onOpenChange={setIsOpen}>
      {files.isLoading && (
        <Button variant="dropdown" disabled label={String(t("other:statement-files-loading", "Loading files..."))} onClick={() => {}} />
      )}
      {files.isError && (
        <Button variant="dropdown" disabled label={String(t("other:statement-files-error", "Could not load files for this statement."))} onClick={() => {}} />
      )}
      {!files.isLoading && !files.isError && fileList.length === 0 && (
        <Button variant="dropdown" disabled label={String(t("other:no-statement-files", "No files available for this statement."))} onClick={() => {}} />
      )}
      {fileList.map(file => (
        <Button
          key={file.s3path}
          variant="dropdown"
          icon={getTypeIcon(file.mimetype)}
          label={file.filename}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleDocumentOpen(file.presignedurl, file.filename, file.mimetype);
          }}
        />
      ))}
    </TableActionsDropdown>
  );
}

function StatementsTable() {
  const { t } = useTranslation();

  const selectedAccount = useAppStore(s => s.selectedAccount) as string;
  const previewStatements = useAppStore(s => s.previewStatements);
  const tenantCurrencyCode = useAppStore(s => s.tenantCurrencyCode);
  const tenantCurrencyLocale = useAppStore(s => s.tenantCurrencyLocale);
  const isTunecore = useUIStateStore(state => state.isTunecore);

  const statements = useGetMyStatements({ aac: selectedAccount, previewstatements: previewStatements });
  const profile = useGetMyProfile();

  const userEmail = profile.data?.email;

  const [statementName, setStatementName] = useState("");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [hasFiles, setHasFiles] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([{ id: "name", desc: true }]);

  const columns = useMemo((): ColumnDef<AccountStatementData>[] => [
    {
      accessorKey: "name",
      header: String(t("common:_name", "Name")),
      size: 250,
      sortingFn: (rowA, rowB) => {
        const dateA = rowA.original.publishdate && rowA.original.publishdate !== "0000-00-00"
          ? Date.parse(rowA.original.publishdate)
          : 0;
        const dateB = rowB.original.publishdate && rowB.original.publishdate !== "0000-00-00"
          ? Date.parse(rowB.original.publishdate)
          : 0;
        return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
      },
      cell: ({ row }) => (
        <>
          <p>{row.original.name}</p>
          <small className="hidden md:inline">
            {"Published "}
            {row.original.publishdate && row.original.publishdate !== "0000-00-00"
              ? format(Date.parse(row.original.publishdate), "yyyy-MM-dd")
              : "unknown"}
          </small>
        </>
      ),
    },
    {
      accessorKey: "closingbalance",
      header: String(t("other:closing-balance", "Closing Balance")),
      size: 200,
      cell: ({ row }) =>
        formatCurrencyString({
          code: row.original.currencycode,
          val: row.original.closingbalanceclientcurrency ?? row.original.closingbalance,
          currencies,
          tenantCurrencyLocale,
          tenantCurrencyCode,
        }),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      size: 60,
      minSize: 60,
      maxSize: 60,
      enablePinning: true,
      meta: { pinSide: "right" },
      cell: ({ row }: { row: Row<AccountStatementData> }) => {
        if (!row.original.hasstatement)
          return null;
        return (
          <StatementFileActionsCell
            suid={row.original.uniqueidentifier}
            aac={selectedAccount}
            userEmail={userEmail}
            previewStatements={previewStatements}
          />
        );
      },
    },
  ], [t, tenantCurrencyLocale, tenantCurrencyCode, selectedAccount, userEmail, previewStatements]);

  const filteredStatements = useMemo(() => {
    const allStatements = statements.data?.statements ?? [];
    const fromStr = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined;
    const toStr = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined;

    return allStatements.filter((statement) => {
      if (fromStr !== undefined || toStr !== undefined) {
        const statementDate = (statement.publishdate !== "" && statement.publishdate !== "0000-00-00")
          ? statement.publishdate.split("T")[0]
          : null;
        if (statementDate === null)
          return false;
        if (fromStr !== undefined && statementDate < fromStr)
          return false;
        if (toStr !== undefined && statementDate > toStr)
          return false;
      }

      if (hasFiles && !statement.hasstatement) {
        return false;
      }

      if (statementName && !statement.name.toLocaleLowerCase().includes(statementName.toLowerCase())) {
        return false;
      }

      return true;
    });
  }, [statements.data?.statements, dateRange?.from, dateRange?.to, statementName, hasFiles]);

  const totalStatements = statements.data?.statements.length ?? 0;
  const filteredCount = filteredStatements.length;

  const statsBlock = useMemo(() => [
    {
      title: t("other:stat-total-statements", "Total statements"),
      value: totalStatements,
      highlight: false,
    },
    {
      title: t("other:stat-filtered-statements", "Filtered"),
      value: filteredCount,
      highlight: true,
    },
  ], [totalStatements, filteredCount, t]);

  const filterContent = (
    <>
      <FormInput
        smallFont
        label={t("other:statement-filter-by-name", "Name")}
        placeholder={`${t("common:filter", "Filter")} ${t("other:statement", "statements")}`}
        value={statementName}
        onChange={e => setStatementName(e.target.value)}
      />
      <DateRangePicker
        smallFont
        label={t("other:statement-filter-by-date-range", "Published Date")}
        initialRange={dateRange}
        onSelect={setDateRange}
        maxDate={new Date()}
        numberOfMonths={2}
        defaultMonth={new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1)}
      />

      <Select value={hasFiles ? "withFiles" : "all"} onValueChange={v => setHasFiles(v === "withFiles")}>
        <SelectTrigger smallFont label={t("other:statement-filter-by-files", "Files")} className="pl-0 w-full">
          <SelectValue placeholder={t("other:all", "All")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t("other:all", "All")}</SelectItem>
          <SelectItem value="withFiles">{t("other:with-files", "With Files")}</SelectItem>
        </SelectContent>
      </Select>
    </>
  );

  const handleClear = useCallback(() => {
    setStatementName("");
    setDateRange(undefined);
    setHasFiles(false);
  }, []);

  return (
    <div className={`${isTunecore ? "bg-layer-inverse" : "bg-white"} rounded-lg p-4 lg:p-8`}>
      {(statements.isLoading || statements.data?.total >= 1) && (
        <Table
          data={filteredStatements}
          columns={columns}
          sorting={sorting}
          onSortingChange={setSorting}
          enableColumnResizing={false}
          statsBlock={statsBlock}
          filterContent={filterContent}
          onClear={handleClear}
          isLoading={statements.isLoading}
          isRefetchingData={statements.isFetching}
        />
      )}

      {!statements.isLoading && (!statements.data || statements.data.total < 1) && (
        <div>
          <Text as="subtitle" className={`mb-2 ${isTunecore ? "text-white" : ""}`}>
            {t("other:no-statements-title", "No Statements Available")}
          </Text>
          <Text className={`${isTunecore ? "text-white" : ""}`}>
            {t("other:no-statements-message", "You will see statements here after your the quarterly distribution, please check back then.")}
          </Text>
          <div className="mt-6">
            <Text as="bodySm" className={`text-xs ${isTunecore ? "text-white" : ""}`}>
              {t("other:no-statements-support-message", "If it seems like we've made a mistake or if you would like a distribution schedule, please reach out to support.")}
            </Text>
          </div>
        </div>
      )}
    </div>
  );
}

export default StatementsTable;
